import type { ReactNode } from 'react'
import { useSettings } from '../../hooks/queries'
import { adjustOf, collageVariables, type CollageCrop, type CollageStyle } from '../../lib/collage'
import { activeGrade } from '../../lib/photoEdit'
import { EditedCanvas } from '../../components/EditedCanvas'

/**
 * The drawing of a collage (1.168.0), one for every place that shows one: the
 * bubble, the preview in the sheet and the defaults in the administration.
 * Two drawings would be two layouts that drift apart, and the preview would
 * promise what the chat does not show.
 *
 * The tiles are whatever the caller hands in (a `Standbild` in the chat, a
 * local picture in the sheet, an empty field in the administration), in the
 * order of the post's photos. The first tile is the big one where a layout
 * has one.
 *
 * **The edit stands here too** (1.176.0): the filter as a variable of the
 * box and the crop of each tile as two of the tile, read by the pictures in
 * it (`.collage__tile img`) -- the rule of `bildStil()`, so the tile shows
 * what the reader's picture shows (`Collage::sourceRect()`).
 *
 * **And the colour grading** (1.177.0) as a drawing over each tile: the
 * photo drawn with chain and grading into a canvas (`EditedCanvas`,
 * `lib/editedPicture.ts`), cropped by the same variables, taking no taps, so
 * the tile below keeps its large view and reactions. For that the caller
 * hands in the address of each tile's picture (`sources`); without it, or
 * until it is drawn, the tile shows the photo with the CSS chain.
 */
export function Collage({ style, tiles, crops, sources }: { style: CollageStyle; tiles: ReactNode[]; crops?: (CollageCrop | undefined)[]; sources?: (string | undefined)[] }) {
  const filters = useSettings().data?.collage?.filters
  const adjust = adjustOf(style)
  return (
    <div
      className="collage"
      data-layout={style.layout}
      data-count={tiles.length}
      style={collageVariables(style, filters)}
    >
      {tiles.map((tile, i) => {
        const crop = crops?.[i]
        return (
          <div
            key={i}
            className="collage__tile"
            style={crop === undefined ? undefined : ({ '--crop-point': `${crop.x}% ${crop.y}%`, '--crop-scale': String(crop.zoom / 100) } as Record<string, string>)}
            data-crop={crop === undefined ? undefined : `${crop.x}/${crop.y}/${crop.zoom}`}
          >
            {/* First, so that what the tile lays over its photo (reactions,
                "+N", the sheet's mark) stays above it; the photo itself is
                hidden once the overlay is there (`app.css`). */}
            {activeGrade(adjust.grade) !== null && sources?.[i] !== undefined && (
              <EditedCanvas className="collage__graded" src={sources[i]} adjust={adjust} filters={filters} marks={{ 'data-collage-graded': '' }} />
            )}
            {tile}
          </div>
        )
      })}
    </div>
  )
}
